/**
 * Protected Route Component
 * Redirects unauthenticated users to login
 */

import { hasRoleOrDepartmentAccess } from '@hrms/shared';
import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { ADMIN_ROLES, isAdminRole, isSuperAdminRole, SUPER_ADMIN_ROLES } from '../utils/roleHelpers';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  /** Employees (role === 'employee') in one of these departments are also allowed,
   *  even if their role isn't in allowedRoles. Matched case-insensitively/trimmed
   *  since department is a free-text field. */
  allowedDepartments?: string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  allowedDepartments,
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const hasAccess = useMemo(() => {
    if (!user) return false;
    if (hasRoleOrDepartmentAccess(user.role, user.department, allowedRoles, allowedDepartments)) {
      return true;
    }
    // Check custom roles with executive or admin privileges
    if (allowedRoles && user.role) {
      if (
        isAdminRole(user.role) &&
        allowedRoles.some((r) => ADMIN_ROLES.includes(r as any))
      ) {
        return true;
      }
      if (
        isSuperAdminRole(user.role) &&
        allowedRoles.some((r) => SUPER_ADMIN_ROLES.includes(r as any))
      ) {
        return true;
      }
    }
    return false;
  }, [user, allowedRoles, allowedDepartments]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  if (user && !hasAccess) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
};
